import { useAuth } from "@/hooks/use-auth";

import { MUM_HUB_WEB_ENABLED } from "./flag";
import { useMumHubEntitlements, useMumHubProfile } from "./use-mum-hub";
import { useMumHubFirebaseAccess } from "./use-mum-hub-firebase";
import type { MumHubEntitlements } from "./api";

/**
 * Who can see Mum Hub on the website (MAR-219, MAR-235).
 *
 * Several ways in, and any one of them is enough:
 *
 * 1. the BUILD FLAG, `VITE_MUM_HUB_WEB_ENABLED=true`, which is the switch that
 *    opens the feature to everybody;
 * 2. the BROWSER OVERRIDE, `localStorage['cch:mum-hub-web'] === '1'`, which
 *    opens it to one browser;
 * 3. an ENTITLEMENT. A signed-in customer whose `GET /api/mum-hub/entitlements`
 *    comes back with `all: true` or with at least one stage sees the feature,
 *    whatever the flag says.
 * 4. a MUM HUB PROFILE, i.e. a due date already on file.
 * 5. FIREBASE REMOTE CONFIG, the same `mum_hub_enabled` switch and
 *    `mum_hub_allowlist` array the iOS/Android app has gated on since
 *    MAR-138. A signed-in customer whose salted email token is in that array
 *    sees Mum Hub on the website exactly as she sees the tab in the app.
 *
 * The fifth arm is MAR-235, and it is the one that closed a real gap rather
 * than a hypothetical one: a tester on the console allow-list had the tab on
 * her phone and the not-found page on capsulechichub.com, because the website
 * had never heard of the parameters the app decides on. Adding a token to the
 * array now admits her on both platforms, with no deploy on either.
 *
 * The third arm is what this module was added for. A tester who has been
 * granted Mum Hub by hand, or is on the allow-list, or has actually bought a
 * stage, must be able to use it without being talked through a devtools
 * console, and needed no backend change: the endpoint already answers exactly
 * this question and the mobile app already gates on it.
 *
 * It is also self-limiting in a way the other two are not. Nobody can put
 * themselves in it: the answer comes from the Worker, keyed to the session
 * cookie, and every page it unlocks then reads its content from that same
 * Worker behind the same checks. The gate decides whether a route EXISTS in
 * the router; it has never been what protects the content.
 *
 * ## The SEO half stays on the build flag alone
 *
 * Prerendering `/mum-hub` and putting it in `sitemap.xml` happen at build
 * time, where there is no customer and no session, so they read
 * `VITE_MUM_HUB_WEB_ENABLED` and nothing else (`scripts/lib/mum-hub-seo.mjs`).
 * An entitled customer reaching the landing page is a person following a
 * link; Google being told the page exists is a decision about the whole site,
 * and only the build flag makes it.
 *
 * The landing ROUTE follows this gate rather than the build flag, because the
 * nav entry it is reached from does: a header link that led to the not-found
 * page would be worse than no link.
 *
 * ## Pending is a third answer, not a kind of hidden
 *
 * For a signed-in customer with the flag off, whether Mum Hub exists is not
 * known until the entitlements read comes back. Answering "no" in the
 * meantime renders the not-found page and then replaces it, which is a flash
 * of 404 on a page she has paid for. So the gate has three states and the
 * router draws nothing gate-dependent while it is `pending`.
 *
 * Signed OUT there is nothing to wait for, so it settles immediately: the
 * entitlements query is not even enabled without a session.
 */

/** Whether an entitlements payload lets her in. */
export function isEntitledToAnything(entitlements: MumHubEntitlements): boolean {
  // `all` OR any stage. Not `offers`, which is what she could still BUY and is
  // populated for someone who owns nothing at all.
  return entitlements.all || Object.keys(entitlements.stages).length > 0;
}

export interface MumHubGateFacts {
  /** The build flag or the browser override. Either one, decided in `flag.ts`. */
  staticEnabled: boolean;
  authLoading: boolean;
  signedIn: boolean;
  entitlements:
    | { status: "idle" }
    | { status: "pending" }
    | { status: "error" }
    | { status: "success"; data: MumHubEntitlements };
  /**
   * Whether she has a Mum Hub profile (a due date on file). The fourth way
   * in: a customer who set up on the app is on its Firebase allow-list,
   * which the website cannot read, and the preview she gets there she must
   * get here too. Adrian hit this with a free tester set up on mobile.
   */
  profile:
    | { status: "idle" }
    | { status: "pending" }
    | { status: "error" }
    | { status: "success"; hasDueDate: boolean };
  /**
   * The Firebase Remote Config decision for this customer: the global switch
   * OR her token being in `mum_hub_allowlist`. `idle` signed out, `pending`
   * while the fetch is in flight, `settled` with the answer. A failed fetch
   * settles as `allowed: false` rather than staying pending, because the
   * module underneath it never rejects.
   */
  firebase: { status: "idle" | "pending" | "settled"; allowed: boolean };
}

export interface MumHubGate {
  /** Draw the feature. */
  visible: boolean;
  /**
   * Visible because of the static flag (build flag or browser override), so
   * the header and footer already carry the entry. False when only an
   * entitlement, a due date or Firebase Remote Config opened the gate.
   */
  viaFlag: boolean;
  /** Not known yet. Draw nothing gate-dependent, and NOT the not-found page. */
  pending: boolean;
}

export function deriveMumHubGate(facts: MumHubGateFacts): MumHubGate {
  // Checked first and on its own, so that with the flag on the feature does
  // not wait for a network read it does not need. A signed-out visitor on a
  // flagged build must see the landing page on the first paint.
  if (facts.staticEnabled) return { visible: true, viaFlag: true, pending: false };

  // Whether there is a session at all is itself a read. Answering "no" here
  // would hide the feature from an entitled customer for the length of it.
  if (facts.authLoading) return { visible: false, viaFlag: false, pending: true };
  if (!facts.signedIn) return { visible: false, viaFlag: false, pending: false };

  const entitled =
    facts.entitlements.status === "success" && isEntitledToAnything(facts.entitlements.data);
  const setUp = facts.profile.status === "success" && facts.profile.hasDueDate;
  // The Firebase arm can only ever open the gate from a SETTLED read. An
  // `allowed: true` carried on a pending or idle status would be a fail-open
  // shape, so the status is checked rather than trusted to be consistent.
  const remote = facts.firebase.status === "settled" && facts.firebase.allowed;
  if (entitled || setUp || remote) return { visible: true, viaFlag: false, pending: false };

  // Every read has settled and none of them lets her in. A failed read settles
  // as NOT visible rather than staying pending for ever: failing closed on a
  // question about a paid feature is right, and a page that spins
  // indefinitely is worse than one that says it is not there.
  const settled = (status: string) =>
    status === "success" || status === "error" || status === "settled";
  if (
    settled(facts.entitlements.status) &&
    settled(facts.profile.status) &&
    settled(facts.firebase.status)
  ) {
    return { visible: false, viaFlag: false, pending: false };
  }
  return { visible: false, viaFlag: false, pending: true };
}

/**
 * The gate, for a component.
 *
 * Calls the entitlements query unconditionally, which is not wasteful: it is
 * `enabled` on there being a session, it shares its cache with the hub page,
 * and with the flag on the answer above is returned before it is consulted.
 */
export function useMumHubGate(): MumHubGate {
  const { customer, isLoading } = useAuth();
  const entitlements = useMumHubEntitlements();
  const profile = useMumHubProfile();
  const firebase = useMumHubFirebaseAccess();

  return deriveMumHubGate({
    staticEnabled: MUM_HUB_WEB_ENABLED,
    authLoading: isLoading,
    signedIn: !!customer,
    entitlements: !customer
      ? { status: "idle" }
      : entitlements.isSuccess
        ? { status: "success", data: entitlements.data }
        : entitlements.isError
          ? { status: "error" }
          : { status: "pending" },
    profile: !customer
      ? { status: "idle" }
      : profile.isSuccess
        ? { status: "success", hasDueDate: !!profile.data.dueDate }
        : profile.isError
          ? { status: "error" }
          : { status: "pending" },
    firebase,
  });
}
